/**
 * 進捗通知(notifications)。申請一覧の赤●とメニューバッジの実体。
 * 一覧を開いた時点で既読化する(decisions.md 2026-08-21 補足B)。
 */
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
  type DocumentReference,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { AppNotification, NotificationKind, NotificationStage } from '@core/lib/types';

/** 未設定(旧データ)は info 扱い */
export function kindOf(n: AppNotification): NotificationKind {
  return n.kind === 'action' ? 'action' : 'info';
}

/**
 * 操作依頼の宛先画面(承認/支払い/受領/完了確認)。info通知は null(申請一覧)。
 * stage未設定の旧action通知は「承認」扱いにする
 */
export function stageOf(n: AppNotification): NotificationStage | null {
  // stage をバッジに反映するのは操作依頼(action)のみ。
  // お知らせ(info)は遷移先ヒントとして stage を持つことがあるが(2026-08-27 ミラー)、バッジはお知らせ欄に付く
  if (kindOf(n) !== 'action') return null;
  if (
    n.stage === 'approve' ||
    n.stage === 'pay' ||
    n.stage === 'receive' ||
    n.stage === 'confirm' ||
    n.stage === 'acct_confirm'
  )
    return n.stage;
  return 'approve';
}

function withId(snap: QueryDocumentSnapshot): AppNotification {
  return { id: snap.id, ...(snap.data() as object) } as AppNotification;
}

/**
 * お知らせ欄(2026-08-26 会議): 自分宛ての通知を既読も含めて取得する。
 * 並び順はクライアント側で新しい順にする(インデックス不要のため)
 */
export async function fetchMyNotifications(userId: string): Promise<AppNotification[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'notifications'), where('user_id', '==', userId), limit(300)),
  );
  const toMillis = (n: AppNotification) => {
    const c = n.created_at as { toMillis?: () => number } | null;
    return c?.toMillis ? c.toMillis() : 0;
  };
  return snap.docs.map(withId).sort((a, b) => toMillis(b) - toMillis(a));
}

/** 自分宛ての未読通知 */
export async function fetchMyUnread(userId: string): Promise<AppNotification[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'notifications'),
      where('user_id', '==', userId),
      where('read', '==', false),
      limit(200),
    ),
  );
  return snap.docs.map(withId);
}

/**
 * 画面を開いた時点で既読にする(補足指示B)。
 * kind を渡すとその種類のみ既読化する(申請一覧=info / 承認・支払い=action)
 */
export async function markAllRead(userId: string, kind?: NotificationKind): Promise<void> {
  const unread = (await fetchMyUnread(userId)).filter((n) => !kind || kindOf(n) === kind);
  await markRead(unread);
}

/**
 * 特定の申請の**お知らせ(info)**だけ既読にする(2026-08-27 変更)。
 * 操作依頼(action)は詳細を開いても消えず、アクションが完了するまで残る
 */
export async function markReadForTransaction(userId: string, transactionId: string): Promise<void> {
  const unread = (await fetchMyUnread(userId)).filter(
    (n) => n.transaction_id === transactionId && kindOf(n) === 'info',
  );
  await markRead(unread);
}

/** 1件のお知らせを既読にする(お知らせ欄の「既読」ボタン・2026-08-27) */
export async function markOneRead(notificationId: string): Promise<void> {
  await updateDoc(doc(getDb(), 'notifications', notificationId), { read: true });
}

/**
 * アクション完了時の既読化(2026-08-27)。
 * 操作依頼(action)は「詳細を開いた時」ではなく「そのアクションが済んだ時」に消える。
 * 例: 承認されたら、両承認者に届いていた承認依頼のバッジがまとめて消える。
 * 各遷移(flowActions)から、次のステップの通知を送る**前**に呼ぶ
 */
export async function markActionDone(transactionId: string): Promise<void> {
  try {
    const snap = await getDocs(
      query(
        collection(getDb(), 'notifications'),
        where('transaction_id', '==', transactionId),
        where('read', '==', false),
        limit(100),
      ),
    );
    // 操作依頼(action)と、そのお知らせミラー(info かつ stage あり)をまとめて既読にする。
    // 申請者向けのステータス更新お知らせ(stage なし)は対象外
    const targets = snap.docs
      .map(withId)
      .filter((n) => kindOf(n) === 'action' || (kindOf(n) === 'info' && n.stage != null));
    await markRead(targets);
  } catch {
    // 既読化の失敗で遷移自体は止めない
  }
}

/**
 * 一覧に表示されていない申請の通知を既読にする(担当が他の人に処理された等で
 * 手元の一覧から消えた申請の通知が、未読のまま残り続けるのを防ぐ)。
 * 表示中の申請の通知は消さない(既読は「詳細」を開いた時のみ・2026-08-26)
 */
export async function markReadExcept(
  userId: string,
  kind: NotificationKind,
  keepTransactionIds: Set<string>,
): Promise<void> {
  const unread = (await fetchMyUnread(userId)).filter(
    (n) => kindOf(n) === kind && !keepTransactionIds.has(n.transaction_id),
  );
  await markRead(unread);
}

/** markReadExcept のページ別版: そのページ宛て(stage)の通知だけを対象にする */
export async function markReadExceptStage(
  userId: string,
  stage: NotificationStage,
  keepTransactionIds: Set<string>,
): Promise<void> {
  const unread = (await fetchMyUnread(userId)).filter(
    (n) => stageOf(n) === stage && !keepTransactionIds.has(n.transaction_id),
  );
  await markRead(unread);
}

/**
 * 自分宛て未読通知のリアルタイム購読(2026-08-26)。
 * ページを更新しなくてもメニューバッジが即時に増減する。戻り値は購読解除関数
 */
export function subscribeMyUnread(
  userId: string,
  onChange: (list: AppNotification[]) => void,
): () => void {
  return onSnapshot(
    query(
      collection(getDb(), 'notifications'),
      where('user_id', '==', userId),
      where('read', '==', false),
      limit(200),
    ),
    (snap) => onChange(snap.docs.map(withId)),
    () => onChange([]),
  );
}

async function markRead(list: AppNotification[]): Promise<void> {
  if (list.length === 0) return;
  const batch = writeBatch(getDb());
  for (const n of list) batch.update(doc(getDb(), 'notifications', n.id), { read: true });
  await batch.commit();
}

/**
 * 複数ユーザーへ同じ通知を送る(状態遷移時に呼ぶ)。
 * お知らせ(info)は**取引×ユーザーごとに1件**の固定IDで上書きする(2026-08-27):
 * 「部門長確認されました」→「承認されました…」のように、常に最新ステータス1行だけが残る
 */
export type NotifyParams = {
  userIds: string[];
  transactionId: string;
  message: string;
  /** action=操作依頼(承認/支払い/受領/完了確認の画面へ) / info=お知らせ(取引ごとに1行で上書き)。省略時 info */
  kind?: NotificationKind;
  /** 操作依頼の宛先画面。action のとき必ず指定する(approve/pay/receive/confirm) */
  stage?: NotificationStage;
};

/** 通知1件ぶんの書き込み内容(まだ送っていない) */
export type NotificationWrite = { ref: DocumentReference; data: Record<string, unknown> };

/**
 * 通知の書き込み内容を組み立てる(送信はしない)。
 * 申請の作成と同じ1回の書き込みにまとめるために使う(2026-09-30: 携帯回線で送信が遅い件)
 */
export function buildNotifications(params: NotifyParams): NotificationWrite[] {
  const targets = [...new Set(params.userIds)].filter(Boolean);
  if (targets.length === 0) return [];
  const isInfo = (params.kind ?? 'info') === 'info';
  const db = getDb();
  const writes: NotificationWrite[] = [];
  for (const userId of targets) {
    if (isInfo) {
      writes.push({
        ref: doc(db, 'notifications', `i_${userId}_${params.transactionId}`),
        data: {
          user_id: userId,
          transaction_id: params.transactionId,
          message: params.message,
          kind: 'info',
          stage: null,
          read: false,
          created_at: serverTimestamp(),
        },
      });
    } else {
      const stage = params.stage ?? 'approve';
      // 操作依頼(バッジの実体)
      writes.push({
        ref: doc(collection(db, 'notifications')),
        data: {
          user_id: userId,
          transaction_id: params.transactionId,
          message: params.message,
          kind: 'action',
          stage,
          read: false,
          created_at: serverTimestamp(),
        },
      });
      // お知らせ欄にも同じ内容を1行で載せる(2026-08-27: 承認者・経理にもお知らせが届くように)。
      // stage を持たせて、クリック時にアクションを行うページへ遷移できるようにする
      writes.push({
        ref: doc(db, 'notifications', `i_${userId}_${params.transactionId}`),
        data: {
          user_id: userId,
          transaction_id: params.transactionId,
          message: params.message,
          kind: 'info',
          stage,
          read: false,
          created_at: serverTimestamp(),
        },
      });
    }
  }
  return writes;
}

export async function notifyUsers(params: {
  userIds: string[];
  transactionId: string;
  message: string;
  /** action=操作依頼(承認/支払い/受領/完了確認の画面へ) / info=お知らせ(取引ごとに1行で上書き)。省略時 info */
  kind?: NotificationKind;
  /** 操作依頼の宛先画面。action のとき必ず指定する(approve/pay/receive/confirm) */
  stage?: NotificationStage;
}): Promise<void> {
  const targets = [...new Set(params.userIds)].filter(Boolean);
  if (targets.length === 0) return;
  const isInfo = (params.kind ?? 'info') === 'info';
  const db = getDb();
  const batch = writeBatch(db);
  for (const userId of targets) {
    if (isInfo) {
      batch.set(doc(db, 'notifications', `i_${userId}_${params.transactionId}`), {
        user_id: userId,
        transaction_id: params.transactionId,
        message: params.message,
        kind: 'info',
        stage: null,
        read: false,
        created_at: serverTimestamp(),
      });
    } else {
      const stage = params.stage ?? 'approve';
      // 操作依頼(バッジの実体)
      batch.set(doc(collection(db, 'notifications')), {
        user_id: userId,
        transaction_id: params.transactionId,
        message: params.message,
        kind: 'action',
        stage,
        read: false,
        created_at: serverTimestamp(),
      });
      // お知らせ欄にも同じ内容を1行で載せる(2026-08-27: 承認者・経理にもお知らせが届くように)。
      // stage を持たせて、クリック時にアクションを行うページへ遷移できるようにする
      batch.set(doc(db, 'notifications', `i_${userId}_${params.transactionId}`), {
        user_id: userId,
        transaction_id: params.transactionId,
        message: params.message,
        kind: 'info',
        stage,
        read: false,
        created_at: serverTimestamp(),
      });
    }
  }
  await batch.commit();
}
